<ct-lib-modal
  #createModal
  class="campaign-create-modal"
  [footer]="createModalFooter"
  [label]="'company-tool.views.campaigns.modal-title' | transloco"
>
  @if (showJobSelect()) {
    <div class="form-floating">
      <ng-select
        id="campaign-booking-job-select"
        class="form-control"
        [appendTo]="'body'"
        [clearable]="true"
        [searchable]="true"
        [formControl]="selectedJobId"
        [placeholder]="
          'ct-library.constants.ng-select.placeholders.placeholder' | transloco
        "
      >
        <ng-template ng-label-tmp>
          <div class="d-flex align-items-center gap-1">
            @if (selectedJob()) {
              <p class="mb-0">{{ selectedJob().title }}</p>
              @if (selectedJob().flexibleWorkLocation) {
                <ct-lib-tag>
                  {{
                    'company-tool.views.jobs.list.table.flexible-facility'
                      | transloco
                  }}
                </ct-lib-tag>
              }
              @for (
                facility of selectedJob().facilities ?? [];
                track facility.city
              ) {
                <ct-lib-tag>{{ facility.city }}</ct-lib-tag>
              }
            } @else {
              <p class="mb-0">
                {{ 'company-tool.views.campaigns.list.all-label' | transloco }}
              </p>
            }
          </div>
        </ng-template>
        <ng-template ng-option-tmp let-jobId="item" let-option="item$">
          @if (
            jobsService.jobsAllowedForCampaigns.find(
              (candidateJob) => candidateJob.id === jobId
            );
            as job
          ) {
            @let tooltipKey =
              jobSelectionDisabled(job)
                ? 'company-tool.views.campaigns.list.job-selection-tooltip-no-rights'
                : null;
            <span
              [tooltip]="tooltipKey ? (tooltipKey | transloco) : ''"
              [tooltipDisabled]="!option.disabled || !tooltipKey"
              class="d-inline-block w-100"
            >
              <div class="d-flex align-items-center gap-1">
                <p class="mb-0">{{ job.title }}</p>
                @if (job.flexibleWorkLocation) {
                  <ct-lib-tag>
                    {{
                      'company-tool.views.jobs.list.table.flexible-facility'
                        | transloco
                    }}
                  </ct-lib-tag>
                }
                @for (
                  facility of job?.facilities ?? [];
                  let i = $index;
                  track facility.city + i
                ) {
                  <ct-lib-tag>{{ facility.city }}</ct-lib-tag>
                }
              </div>
            </span>
          }
        </ng-template>
        @for (job of jobsService.jobsAllowedForCampaigns; track job.id) {
          <ng-option [value]="job.id" [disabled]="jobSelectionDisabled(job)">
            <div class="d-flex align-items-center gap-1">
              <p class="mb-0">{{ job.title }}</p>
              @if (job.flexibleWorkLocation) {
                <ct-lib-tag>
                  {{
                    'company-tool.views.jobs.list.table.flexible-facility'
                      | transloco
                  }}
                </ct-lib-tag>
              }
              @for (
                facility of job?.facilities ?? [];
                let i = $index;
                track facility.city + i
              ) {
                <ct-lib-tag>{{ facility.city }}</ct-lib-tag>
              }
            </div>
          </ng-option>
        }
      </ng-select>
      <label for="campaign-booking-job-select">{{
        'company-tool.views.campaigns.list.select-label' | transloco
      }}</label>
    </div>
  } @else if (selectedJob(); as lockedJob) {
    <div class="locked-job">
      <span class="label" id="campaign-booking-locked-job-label">
        {{ 'company-tool.views.campaigns.locked-job-label' | transloco }}
      </span>
      <h4 class="title" aria-labelledby="campaign-booking-locked-job-label">
        {{ lockedJob.title }}
      </h4>
    </div>
  }
  @if (selectedJob()) {
    <app-campaign-products-publish
      #campaignProductsPublishComponent
      [submitAttempt]="false"
      [slotType]="selectedJob()?.slotType"
      [bookedPlatformIds]="bookedPlatformIds()"
    ></app-campaign-products-publish>
  }
</ct-lib-modal>

<ng-template #createModalFooter>
  <div class="modal-footer-custom">
    <app-button variant="white-grey" class="left-button" (click)="cancel()">
      {{ 'company-tool.views.campaigns.new-campaign-modal.cancel' | transloco }}
    </app-button>
    <app-button
      (click)="startBooking()"
      [loading]="submitting()"
      [disabled]="
        !campaignProductsPublishComponent?.allSelectedPlatforms()?.length ||
        !selectedJob()
      "
    >
      {{ 'company-tool.views.campaigns.new-campaign-modal.book' | transloco }}
    </app-button>
  </div>
</ng-template>

<ct-lib-modal
  #confirmModal
  class="campaign-confirm-modal"
  [small]="true"
  [footer]="confirmModalFooter"
  [label]="
    (isPaymentMethodStep()
      ? 'company-tool.snippets.payment-method-choice.title'
      : 'company-tool.views.campaigns.modal-title'
    ) | transloco
  "
  [closeable]="!isPaymentBusy()"
  [hideFooter]="isPaymentBusy() && !isPaymentMethodStep()"
  (onClose)="onConfirmModalClose()"
>
  @if (isPaymentMethodStep()) {
    <app-payment-method-choice #paymentMethodChoice [busy]="isPaymentBusy()" />
  } @else if (showsSummary()) {
    <app-campaign-summary-list
      [platforms]="campaignProductsPublishComponent?.summaryPlatforms()"
      [contracts]="
        campaignProductsPublishComponent?.contractSelector?.selectedContracts()
      "
      [price]="campaignProductsPublishComponent?.totalPrice()"
    >
    </app-campaign-summary-list>
    @if (bookingConfirmation.invoicePayment() || pendingInvoiceEnable()) {
      <p class="mt-3 mb-0">
        {{ 'company-tool.snippets.invoice-booking.invoice-note' | transloco }}
      </p>
    }
  } @else {
    <div class="campaign-payment" [class.is-busy]="isPaymentBusy()">
      <app-payment-checkout
        #paymentCheckout
        [showLoader]="false"
        (paymentCompleted)="bookCampaign(paymentCheckout.sessionId())"
        (paymentMounted)="onPaymentMounted()"
        (paymentFailed)="onPaymentFailed()"
      ></app-payment-checkout>
      @if (isPaymentBusy()) {
        <div class="booking-overlay" aria-busy="true" aria-live="polite">
          <ct-lib-spinner [size]="40"></ct-lib-spinner>
          <p>
            {{ 'company-tool.snippets.payment-checkout.loading' | transloco }}
          </p>
        </div>
      }
    </div>
  }
</ct-lib-modal>

<ng-template #confirmModalFooter>
  <div class="modal-footer-custom">
    <app-button
      variant="white-grey"
      class="left-button"
      (click)="
        isPaymentMethodStep() ? backFromPaymentMethod() : confirmModal.close()
      "
      [loading]="submitting()"
      [disabled]="submitting()"
    >
      {{
        (isPaymentMethodStep()
          ? 'company-tool.snippets.payment-method-choice.back'
          : 'company-tool.views.campaigns.new-campaign-confirm-modal.cancel'
        ) | transloco
      }}
    </app-button>
    @if (isPaymentMethodStep()) {
      <app-button
        [loading]="isPaymentBusy()"
        [disabled]="isPaymentBusy()"
        (click)="confirmPaymentMethod()"
      >
        {{ 'company-tool.snippets.payment-method-choice.continue' | transloco }}
      </app-button>
    } @else if (showsSummary()) {
      <app-button
        (click)="confirmBooking()"
        [loading]="submitting()"
        [disabled]="
          !campaignProductsPublishComponent?.allSelectedPlatforms()?.length
        "
      >
        {{
          ((bookingConfirmation.invoicePayment() || pendingInvoiceEnable()) &&
          !isCampaignFree()
            ? 'company-tool.snippets.invoice-booking.confirm'
            : 'company-tool.views.campaigns.new-campaign-confirm-modal.confirm'
          ) | transloco
        }}
      </app-button>
    }
  </div>
</ng-template>
